import { useTranslation } from 'react-i18next';
import {
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Send,
  Package,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

type StatusType =
  | 'pending'
  | 'pending_approval'
  | 'pending_extra_approval'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'draft'
  | 'sent'
  | 'confirmed'
  | 'completed'
  | 'converted'
  | 'partially_received'
  | 'has_discrepancies';

interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const statusConfig: Record<
  StatusType,
  { color: string; icon: React.ElementType; labelKey: string }
> = {
  pending: {
    color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    icon: Clock,
    labelKey: 'purchasing.pending',
  },
  pending_approval: {
    color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    icon: Clock,
    labelKey: 'purchasing.pendingApproval',
  },
  pending_extra_approval: {
    color: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',
    icon: AlertTriangle,
    labelKey: 'purchasing.pendingExtraApproval',
  },
  pending_review: {
    color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    icon: Clock,
    labelKey: 'purchasing.pendingReview',
  },
  approved: {
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
    icon: CheckCircle,
    labelKey: 'purchasing.approved',
  },
  rejected: {
    color: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800',
    icon: XCircle,
    labelKey: 'purchasing.rejected',
  },
  cancelled: {
    color: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800',
    icon: XCircle,
    labelKey: 'purchasing.cancelled',
  },
  draft: {
    color: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700',
    icon: FileText,
    labelKey: 'purchasing.draft',
  },
  sent: {
    color: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800',
    icon: Send,
    labelKey: 'purchasing.sent',
  },
  confirmed: {
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
    icon: CheckCircle,
    labelKey: 'purchasing.confirmed',
  },
  completed: {
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
    icon: CheckCircle,
    labelKey: 'purchasing.completed',
  },
  converted: {
    color: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
    icon: Package,
    labelKey: 'purchasing.convertedToPO',
  },
  partially_received: {
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
    icon: Loader2,
    labelKey: 'purchasing.partiallyReceived',
  },
  has_discrepancies: {
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
    icon: AlertTriangle,
    labelKey: 'purchasing.hasDiscrepancies',
  },
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
  lg: 'px-3 py-1.5 text-sm gap-2',
};

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

export default function StatusBadge({
  status,
  size = 'md',
  showIcon = true,
}: StatusBadgeProps) {
  const { t } = useTranslation();

  const config = statusConfig[status as StatusType] || {
    color: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700',
    icon: FileText,
    labelKey: `purchasing.${status}`,
  };

  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center font-medium rounded-md border ${config.color} ${sizeClasses[size]}`}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      <span>{t(config.labelKey) || status}</span>
    </span>
  );
}
